import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwtDecode from 'jwt-decode';
import { SentryService } from '../services/sentry.service';

@Injectable()
export class AuthService {
    constructor(private router: Router, private sentry: SentryService) {}

    public getRedirectPath(): string | null {
        return localStorage.getItem('redirect');
    }

    public getRolesArray(): string[] {
        return this.decodeToken()?.['cognito:groups'] ?? [];
    }

    public getToken(): string {
        return localStorage.getItem('id_token') ?? '';
    }

    public getUsername(): string {
        return this.decodeToken()?.['cognito:username'] ?? '';
    }

    public getUserProfile() {
        const decodedToken = this.decodeToken();
        return {
            email: decodedToken?.email ?? '',
            username: decodedToken?.['cognito:username'] ?? '',
        };
    }

    public isLoggedIn(): boolean {
        const token = this.getToken();
        return !(
            !token ||
            !this.isTokenWellFormed(token) ||
            this.isTokenExpired(token)
        );
    }

    public setRedirectPath(path: string) {
        return localStorage.setItem('redirect', path);
    }

    public setToken(token: string) {
        localStorage.setItem('id_token', token);
    }

    public signOut() {
        sessionStorage.clear();
        this.unsetToken();
        this.sentry.resetScope();
        this.router.navigateByUrl('/');
    }

    public unsetToken() {
        localStorage.removeItem('id_token');
    }

    /**
     * The type is hard coded here because no one from external world should know what exactly is inside the token.
     */
    private decodeToken(): {
        'cognito:groups': string[];
        'cognito:username': string;
        email: string;
    } | null {
        const token = this.getToken();

        return token ? jwtDecode(token) : null;
    }

    private isTokenExpired(token?: string): boolean {
        if (!token) {
            return true;
        }

        const decoded: { exp: string } = jwtDecode(token);

        const expirationEpoch = parseInt(decoded.exp);

        if (!expirationEpoch || isNaN(expirationEpoch)) {
            return true;
        }

        return new Date().getTime() / 1000 > expirationEpoch;
    }

    private isTokenWellFormed(token: string) {
        const [header, body] = token.split('.');
        try {
            const headerString = window.atob(header);
            const headerParsed: { kid: unknown } = JSON.parse(headerString);
            const bodyString = window.atob(body);
            const bodyParsed: { at_hash: unknown } = JSON.parse(bodyString);

            return headerParsed.kid && bodyParsed.at_hash;
        } catch (e) {
            return false;
        }
    }
}
